/*
 *
 * MobileNavBar
 *
 * @version 1.0.0
 * @author Davide Polci
 * @since 21-Oct-2024
 */

import React from 'react'
import PropTypes from 'prop-types'
import { NavLink } from 'react-router-dom'
import { useIntl } from 'react-intl'
import {
  AddressBook,
  CalendarCheckmark,
  CallPayment,
  Dismiss,
  Home,
  LaptopSetting,
  LinkPayment,
  List,
  Location,
  NewCalendar,
  NewPayment,
  PaymentCheckmark,
  PaymentToCard,
  People,
  RecurrentPayment,
  ShoppingBag
} from '@nexidigital/iconlibrary'
import PortalIconButton from '@nexidigital/portaliconbutton'
import { getLabels, getNavigationItems, navigationMessages } from 'Schemas/navigationMap'
import { routesList } from 'Schemas/routesList'
import './MobileNavBar.scss'

const MobileNavBar = ({ closeMobileMenu, userPermissions, type, getSubscriptionArray, userProfile, actions }) => {
  const intl = useIntl()

  const visibilityContext = { userPermissions, getSubscriptionArray, userProfile, type }
  const visibleItems = getNavigationItems(visibilityContext)
  const isVisible = (id) => visibleItems.some((item) => item.id === id)
  const linkClassname = (isActive) =>
    `mobile_navbar_item ${isActive ? 'mobile_navbar_active_link' : 'mobile_navbar_link'}`
  const handlePblQuickActionClick = () => {
    actions.doSetPblSourcePage?.('dashboard')
    closeMobileMenu()
  }
  const handleBookingQuickActionClick = () => {
    actions.doSetBookingSourcePage?.('dashboard')
    closeMobileMenu()
  }

  const labels = getLabels(intl, navigationMessages)

  return (
    <nav className='mobile_navbar_container'>
      <div className='mobile_navbar_menutitle_container'>
        <h2 className='mobile_navbar_menutitle'>{labels.menu}</h2>
        {/* <button className='mobile_navbar_menutitle_icon' onClick={closeMobileMenu} aria-label={labels.close}>
          <Dismiss size='20' type='line' />
        </button> */}
        <PortalIconButton ariaLabel={labels.close} icon={<Dismiss size='20' type='line' />} onClick={closeMobileMenu} />
      </div>

      <div className='mobile_navbar_menuitems_container'>
        <div className='mobile_navbar_item_container'>
          <NavLink
            onClick={closeMobileMenu}
            to={routesList.goToDashboard}
            className={({ isActive }) => linkClassname(isActive)}>
            {({ isActive }) => (
              <>
                <span className='mobile_navbar_item_line' aria-hidden='true' />
                <Home size='24' type={isActive ? 'fill' : 'line'} />
                <span>{labels.home}</span>
              </>
            )}
          </NavLink>
        </div>

        {isVisible('customers') && (
          <div className='mobile_navbar_item_container'>
            <NavLink
              onClick={closeMobileMenu}
              to={routesList.customers.goToCustomers}
              className={({ isActive }) => linkClassname(isActive)}>
              {({ isActive }) => (
                <>
                  <span className='mobile_navbar_item_line' aria-hidden='true' />
                  <AddressBook size='24' type={isActive ? 'fill' : 'line'} />
                  <span>{labels.customers}</span>
                </>
              )}
            </NavLink>
          </div>
        )}

        {(isVisible('payByLinkQuickAction') || isVisible('payToCardQuickAction') || isVisible('bookingQuickAction') || isVisible('motoQuickAction') || isVisible('cardCheckQuickAction')) && (
          <div className='mobile_navbar_menuitems_with_title_container'>
            <p className='mobile_navbar_item_title'>{labels.quickActions}</p>

            {isVisible('payByLinkQuickAction') && (
              <div className='mobile_navbar_item_container'>
                <NavLink
                  onClick={handlePblQuickActionClick}
                  to={routesList.pblManagement.goToNewPbl}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='mobile_navbar_item_line' aria-hidden='true' />
                      <NewPayment size='24' type={isActive ? 'fill' : 'line'} />
                      <span>{labels.quickActionPayByLink}</span>
                    </>
                  )}
                </NavLink>
              </div>
            )}

            {isVisible('bookingQuickAction') && (
              <div className='mobile_navbar_item_container'>
                <NavLink
                  onClick={handleBookingQuickActionClick}
                  to={routesList.bookingManagement.goToCreateBookingPayByLink}
                  state={{ bookingSourcePage: 'dashboard' }}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='mobile_navbar_item_line' aria-hidden='true' />
                      <NewCalendar size='24' type={isActive ? 'fill' : 'line'} />
                      <span>{labels.newBooking}</span>
                    </>
                  )}
                </NavLink>
              </div>
            )}

            {isVisible('motoQuickAction') && (
              <div className='mobile_navbar_item_container'>
                <NavLink
                  onClick={closeMobileMenu}
                  to={routesList.motoRoutes.goToMoto}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='mobile_navbar_item_line' aria-hidden='true' />
                      <CallPayment size='24' type={isActive ? 'fill' : 'line'} />
                      <span>{labels.moto}</span>
                    </>
                  )}
                </NavLink>
              </div>
            )}

            {isVisible('cardCheckQuickAction') && (
              <div className='mobile_navbar_item_container'>
                <NavLink
                  onClick={closeMobileMenu}
                  to={routesList.cardCheck.goToCardCheck}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='mobile_navbar_item_line' aria-hidden='true' />
                      <PaymentCheckmark size='24' type={isActive ? 'fill' : 'line'} />
                      <span>{labels.cardCheck}</span>
                    </>
                  )}
                </NavLink>
              </div>
            )}

            {isVisible('payToCardQuickAction') && (
              <div className='mobile_navbar_item_container'>
                <NavLink
                  onClick={closeMobileMenu}
                  to={routesList.payToCard.goToPayToCard}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='mobile_navbar_item_line' aria-hidden='true' />
                      <PaymentToCard size='24' type={isActive ? 'fill' : 'line'} />
                      <span>{labels.quickActionPayToCard}</span>
                    </>
                  )}
                </NavLink>
              </div>
            )}
          </div>
        )}

        {(isVisible('orders') || isVisible('operations') || isVisible('subscriptions')) && (
          <div className='mobile_navbar_menuitems_with_title_container'>
            <p className='mobile_navbar_item_title'>{labels.payments}</p>

            {isVisible('orders') && (
              <div className='mobile_navbar_item_container'>
                <NavLink
                  onClick={closeMobileMenu}
                  to={routesList.orderManagementRoutes.goToOrderManagement}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='mobile_navbar_item_line' aria-hidden='true' />
                      <ShoppingBag size='24' type={isActive ? 'fill' : 'line'} />
                      <span>{labels.ordersManagement}</span>
                    </>
                  )}
                </NavLink>
              </div>
            )}

            {isVisible('operations') && (
              <div className='mobile_navbar_item_container'>
                <NavLink
                  onClick={closeMobileMenu}
                  to={routesList.operationManagement.goToOperationManagement}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='mobile_navbar_item_line' aria-hidden='true' />
                      <List size='24' type={isActive ? 'fill' : 'line'} />
                      <span>{labels.operationsManagement}</span>
                    </>
                  )}
                </NavLink>
              </div>
            )}

            {isVisible('subscriptions') && (
              <div className='mobile_navbar_item_container'>
                <NavLink
                  onClick={closeMobileMenu}
                  to={routesList.subscriptionManagementRoutes.goToSubscriptionPlan}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='mobile_navbar_item_line' aria-hidden='true' />
                      <RecurrentPayment size='24' type={isActive ? 'fill' : 'line'} />
                      <span>{labels.subscriptionManagement}</span>
                    </>
                  )}
                </NavLink>
              </div>
            )}
          </div>
        )}

        {(isVisible('storeManagement') || isVisible('terminalsManagement') || isVisible('usersManagement')) && (
          <div className='mobile_navbar_menuitems_with_title_container'>
            <p className='mobile_navbar_item_title'>{labels.configuration}</p>
            {isVisible('storeManagement') && (
              <div className='mobile_navbar_item_container'>
                <NavLink
                  onClick={closeMobileMenu}
                  to={routesList.pointsOfSales.goToPointsOfSales}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='mobile_navbar_item_line' aria-hidden='true' />
                      <Location size='24' type={isActive ? 'fill' : 'line'} />
                      <span>{labels.stores}</span>
                    </>
                  )}
                </NavLink>
              </div>
            )}

            {isVisible('terminalsManagement') && (
              <div className='mobile_navbar_item_container'>
                <NavLink
                  onClick={closeMobileMenu}
                  to={routesList.terminalsManagement.goToTerminalsManagement}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='mobile_navbar_item_line' aria-hidden='true' />
                      <LaptopSetting size='24' type={isActive ? 'fill' : 'line'} />
                      <span>{labels.manageTerminals}</span>
                    </>
                  )}
                </NavLink>
              </div>
            )}

            {isVisible('usersManagement') && (
              <div className='mobile_navbar_item_container'>
                <NavLink
                  onClick={closeMobileMenu}
                  to={routesList.users.goToUsers}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='mobile_navbar_item_line' aria-hidden='true' />
                      <People size='24' type={isActive ? 'fill' : 'line'} />
                      <span>{labels.users}</span>
                    </>
                  )}
                </NavLink>
              </div>
            )}
          </div>
        )}

        {(isVisible('payByLink') || isVisible('bookingManagement')) && (
          <div className='mobile_navbar_menuitems_with_title_container'>
            <p className='mobile_navbar_item_title'>{labels.services}</p>
            {isVisible('payByLink') && (
              <div className='mobile_navbar_item_container'>
                <NavLink
                  onClick={closeMobileMenu}
                  to={routesList.pblManagement.goToPblManagement}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='mobile_navbar_item_line' aria-hidden='true' />
                      <LinkPayment size='24' type={isActive ? 'fill' : 'line'} />
                      <span>{labels.payByLink}</span>
                    </>
                  )}
                </NavLink>
              </div>
            )}

            {isVisible('bookingManagement') && (
              <div className='mobile_navbar_item_container'>
                <NavLink
                  onClick={closeMobileMenu}
                  to={routesList.bookingManagement.goToBookingManagement}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='mobile_navbar_item_line' aria-hidden='true' />
                      <CalendarCheckmark size='24' type={isActive ? 'fill' : 'line'} />
                      <span>{labels.bookingManagement}</span>
                    </>
                  )}
                </NavLink>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  )
}

MobileNavBar.propTypes = {
  closeMobileMenu: PropTypes.func.isRequired,
  userPermissions: PropTypes.array,
  type: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  getSubscriptionArray: PropTypes.array,
  userProfile: PropTypes.object,
  actions: PropTypes.object
}

MobileNavBar.defaultProps = {
  closeMobileMenu: () => {},
  userPermissions: [],
  type: '',
  getSubscriptionArray: [],
  userProfile: {},
  actions: {}
}

export default MobileNavBar
